import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { useToast } from './ui/Toast';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { formatEUR } from '../lib/format';

interface AccountCredit {
  credit: number;
  reserved: number;
  balance: number;
  unbilled: number;
  payable: number;
  iban: string | null;
}

/**
 * Account credit (migration 136). What is left of the customer's credit notes after the monthly
 * invoices took their share — it comes off the next invoice by itself. It is paid out only when
 * the account is in credit and the customer asks: a transfer to the IBAN on file, requested here
 * by a manager and approved by ANOTHER one on the Refunds page. Never automatic.
 */
export default function CustomerAccountCredit({
  customerId,
  canRequest,
}: {
  customerId: string;
  canRequest: boolean;
}) {
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  const [data, setData] = useState<AccountCredit | null>(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiClient
      .get<AccountCredit>(`/refunds/account-credit/${customerId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null)); // not a role that sees money — no card
  }, [customerId]);
  useEffect(load, [load]);

  if (!data || (data.credit <= 0 && data.reserved <= 0)) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await apiClient.post(
        `/refunds/account-credit/${customerId}/payout`,
        { amount: Number(amount), reason: reason.trim() },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message: t('relations.accountCredit.toastRequested'),
      });
      setOpen(false);
      setAmount('');
      setReason('');
      load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border rounded p-4 bg-card text-sm space-y-2">
      <h2 className="font-semibold">{t('relations.accountCredit.title')}</h2>
      <div className="flex flex-wrap gap-x-8 gap-y-1">
        <span>
          {t('relations.accountCredit.unusedCredit')} <strong>{formatEUR(data.credit)}</strong>
        </span>
        {data.reserved > 0 && (
          <span>
            {t('relations.accountCredit.payoutInProgress', { amount: formatEUR(data.reserved) })}
          </span>
        )}
        <span>
          {t('relations.accountCredit.payable')} <strong>{formatEUR(data.payable)}</strong>
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{t('relations.accountCredit.explain')}</p>
      {data.reserved > 0 && (
        <Link to="/refunds" className="text-xs text-primary underline">
          {t('relations.accountCredit.seeRefunds')}
        </Link>
      )}
      {canRequest && data.payable > 0 && !open && (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={!data.iban}>
          {t('relations.accountCredit.payOut')}
        </Button>
      )}
      {canRequest && data.payable > 0 && !data.iban && (
        <p className="text-xs text-destructive">{t('relations.accountCredit.noIban')}</p>
      )}
      {open && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs">
              {t('relations.accountCredit.amountMax', { amount: formatEUR(data.payable) })}
            </span>
            <Input
              className="h-8 w-32"
              type="number"
              min={0}
              step="0.01"
              max={data.payable}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <label className="flex min-w-[16rem] flex-1 flex-col gap-1">
            <span className="text-xs">{t('relations.accountCredit.reason')}</span>
            <Input className="h-8" value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <span className="text-xs text-muted-foreground">
            {t('relations.accountCredit.to')} <span className="font-mono">{data.iban}</span>
          </span>
          <Button
            size="sm"
            disabled={
              busy ||
              !(Number(amount) > 0) ||
              Number(amount) > data.payable + 0.005 ||
              reason.trim().length < 10
            }
            onClick={() => void submit()}
          >
            {t('relations.accountCredit.request')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            {t('relations.accountCredit.cancel')}
          </Button>
        </div>
      )}
    </section>
  );
}
